import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { motion } from "framer-motion";
import { Share2, Copy, Trophy, XCircle } from "lucide-react";
import { toast } from "sonner";

const ResultModal = ({
  isOpen,
  onClose,
  gameStatus,
  guesses,
  feedbacks,
  revealData,
  movieData,
  streak,
}) => {
  const isWin = gameStatus === "won";

  const generateShareText = () => {
    const emojiGrid = feedbacks
      .map((row) =>
        row
          .map((state) => {
            if (state === "correct") return "🟩";
            if (state === "present") return "🟨";
            return "⬜";
          })
          .join("")
      )
      .join("\n");

    return `GuessTheBolly #${movieData?.day} ${isWin ? guesses.length : "X"}/${6}\n\n${emojiGrid}\n\nPlay at: ${window.location.origin}`;
  };

  const handleShare = async () => {
    const shareText = generateShareText();

    if (navigator.share) {
      try {
        await navigator.share({
          title: "GuessTheBolly",
          text: shareText,
        });
      } catch (err) {
        // User cancelled or share failed, fall back to copy
        copyToClipboard(shareText);
      }
    } else {
      copyToClipboard(shareText);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  // Generate emoji grid for display
  const emojiGrid = feedbacks.map((row, rowIndex) => (
    <div key={rowIndex} className="flex gap-0.5 justify-center">
      {row.map((state, cellIndex) => (
        <span
          key={cellIndex}
          className="text-lg"
          data-testid={`result-emoji-${rowIndex}-${cellIndex}`}
        >
          {state === "correct" ? "🟩" : state === "present" ? "🟨" : "⬜"}
        </span>
      ))}
    </div>
  ));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`glass border-white/10 max-w-sm ${
          isWin ? "border-gold/30" : ""
        }`}
        data-testid="result-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-center">
            {isWin ? (
              <span className="text-gold flex items-center justify-center gap-2">
                <Trophy className="w-6 h-6" />
                You Got It!
              </span>
            ) : (
              <span className="text-cinema-red flex items-center justify-center gap-2">
                <XCircle className="w-6 h-6" />
                Game Over
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Score Display */}
          <div className="text-center">
            <p className="text-text-muted text-sm font-body mb-1">
              Day {movieData?.day}
            </p>
            <p className="text-4xl font-display font-bold text-text-primary">
              {isWin ? guesses.length : "X"}/{6}
            </p>
          </div>

          {/* Movie Title Reveal */}
          {revealData && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-center py-3 bg-dark-subtle rounded-lg"
            >
              <p className="text-xs text-text-muted font-body mb-1">
                The movie was
              </p>
              <p
                className="text-xl font-display font-bold text-gold"
                data-testid="revealed-title"
              >
                {revealData.title}
              </p>
            </motion.div>
          )}

          {/* Emoji Grid */}
          <div
            className="py-3 space-y-0.5"
            data-testid="result-emoji-grid"
          >
            {emojiGrid}
          </div>

          {/* Fun Fact */}
          {revealData?.fun_fact && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="p-4 glass rounded-lg"
            >
              <p className="text-xs text-gold font-body font-semibold mb-2">
                Fun Fact
              </p>
              <p className="text-sm text-text-secondary font-body leading-relaxed">
                {revealData.fun_fact}
              </p>
            </motion.div>
          )}

          {/* Tomorrow's Teaser */}
          {revealData?.teaser && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="p-4 border border-gold/20 rounded-lg bg-gold/5"
            >
              <p className="text-xs text-cinema-red font-body font-semibold mb-2">
                Tomorrow's Teaser
              </p>
              <p className="text-sm text-text-primary font-body italic">
                "{revealData.teaser}"
              </p>
            </motion.div>
          )}

          {/* Streak */}
          {isWin && streak > 0 && (
            <div className="flex justify-center">
              <div className="glass px-4 py-2 rounded-full flex items-center gap-2">
                <span className="text-gold text-xl">🔥</span>
                <span className="text-text-primary font-body font-bold text-lg">
                  {streak}
                </span>
                <span className="text-text-muted text-sm font-body">
                  day streak!
                </span>
              </div>
            </div>
          )}

          {/* Share Button */}
          <motion.button
            onClick={handleShare}
            className="w-full py-3 bg-gold text-dark font-body font-semibold rounded-lg flex items-center justify-center gap-2"
            whileTap={{ scale: 0.98 }}
            data-testid="share-button"
          >
            <Share2 className="w-5 h-5" />
            Share Result
          </motion.button>

          <button
            onClick={() => copyToClipboard(generateShareText())}
            className="w-full py-2 text-text-muted text-sm font-body flex items-center justify-center gap-2 hover:text-text-secondary transition-colors"
            data-testid="copy-button"
          >
            <Copy className="w-4 h-4" />
            Copy to Clipboard
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ResultModal;
